// ImportWizard.js (cloud-admin-portal)
//
// Off-the-Shelf admin pipeline import wizard — the SAME multi-step BYOP import
// flow (Source & parse · Inputs & parameters · Dockerfile), but driven against
// the admin-auth, platform-owned OTS endpoints (/api/super-admin/ots/import/*).
// On finish the imported pipeline is attached to `categoryId` (released='N').
// Adapted from frontend/src/byop/ImportWizard.js: Bearer auth + OTS endpoints +
// an onDone() callback instead of a page redirect.
import React, { useState } from "react";
import API_BASE from "../config";
import InputsParamsTable, { mergeDetected, splitValues } from "./InputsParamsTable";
import "./ImportWizard.css";

const STEPS = ["Source & parse", "Inputs & parameters", "Container image"];
const BASE = `${API_BASE}/api/super-admin/ots/import`;

function authHeaders(extra) {
    return { Authorization: "Bearer " + (localStorage.getItem("idToken") || ""), ...(extra || {}) };
}
async function apiJSON(url, opts = {}) {
    const res = await fetch(url, { credentials: "include", ...opts, headers: { ...authHeaders(), ...(opts.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
}

export default function ImportWizard({ categoryId, categoryName, onDone, onCancel }) {
    const [step, setStep] = useState(1);
    const [byopId, setByopId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [dockerWarnings, setDockerWarnings] = useState([]);

    const [pipelineName, setPipelineName] = useState("");
    const [sourceTab, setSourceTab] = useState("github");
    const [zipFile, setZipFile] = useState(null);
    const [githubUrl, setGithubUrl] = useState("");
    const [githubRef, setGithubRef] = useState("");
    const [githubToken, setGithubToken] = useState("");
    const [report, setReport] = useState(null);
    const [mainScript, setMainScript] = useState("");
    const [mainScriptCandidates, setMainScriptCandidates] = useState([]);

    const [values, setValues] = useState([]);
    const [dockerMode, setDockerMode] = useState("image"); // 'none' (container-complete) | 'image'
    const [imageUrl, setImageUrl] = useState("");

    const saveStep = async (body) => {
        if (!byopId) return;
        await apiJSON(`${BASE}/${byopId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    };

    // ---- Step 1 ----
    const startImport = async () => {
        setError("");
        if (!/^[A-Za-z0-9_ -]{1,64}$/.test(pipelineName.trim())) { setError("Pipeline name: 1–64 letters, numbers, spaces, hyphens, underscores."); return; }
        if (sourceTab === "zip" && !zipFile) { setError("Choose a .zip file to upload."); return; }
        if (sourceTab === "github" && !/^https:\/\/github\.com\/[\w.-]+\/[\w.-]+?(?:\.git|\/tree\/[\w./-]+)?\/?$/.test(githubUrl)) { setError("Enter a valid GitHub repo URL — https://github.com/owner/repo (optionally with /tree/<branch-or-tag>)."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("pipelineName", pipelineName);
            if (sourceTab === "zip") {
                fd.append("file", zipFile);
            } else {
                fd.append("githubUrl", githubUrl);
                if (githubRef.trim()) fd.append("ref", githubRef.trim());
                if (githubToken.trim()) fd.append("token", githubToken.trim());
            }
            const d = await apiJSON(`${BASE}/start`, { method: "POST", body: fd });
            setByopId(d.byopId);
            setReport(d.parse_report || {});
            setValues(mergeDetected(d.parse_report && d.parse_report.inputs, d.parse_report && d.parse_report.parameters));
            setMainScript(d.main_script || (d.parse_report && d.parse_report.mainScript) || "");
            setMainScriptCandidates((d.parse_report && d.parse_report.mainScriptCandidates) || []);
            if (d.parse_report && d.parse_report.containersComplete) setDockerMode("none");
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    const chooseMain = async (v) => { setMainScript(v); if (v) { try { await saveStep({ main_script: v }); } catch (e) { setError(e.message); } } };

    const proceedFromStep1 = async () => {
        if (mainScriptCandidates.length > 1 && !mainScript) { setError("Please choose the main Nextflow script."); return; }
        setError(""); setBusy(true);
        try { await saveStep({ import_step: 2 }); setStep(2); } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    // ---- Steps 2–3 ----
    const goNext = async () => {
        setError(""); setBusy(true);
        try {
            if (step === 2) {
                const s = splitValues(values);
                if (s.inputs.concat(s.parameters).some((x) => !x.name || !x.flag)) throw new Error("Every row needs a name and a flag.");
                for (const p of s.parameters) {
                    if (p.type === "enum") {
                        if (!p.choices || !p.choices.length) throw new Error(`Parameter "${p.name}": enter comma-separated options (e.g. Y,N) for the enum.`);
                        if (p.default !== "" && p.default != null && !p.choices.map(String).includes(String(p.default)))
                            throw new Error(`Parameter "${p.name}": the default "${p.default}" must be one of the options (${p.choices.join(", ")}).`);
                    }
                    if (p.type === "list") {
                        if (!p.choices || !p.choices.length) throw new Error(`Parameter "${p.name}": enter comma-separated options for the list.`);
                        if (p.default !== "" && p.default != null) {
                            const toks = String(p.default).split(",").map((t) => t.trim()).filter(Boolean);
                            if (toks.some((t) => !p.choices.map(String).includes(t))) throw new Error(`Parameter "${p.name}": each list default must be one of the options (${p.choices.join(", ")}).`);
                        }
                    }
                    if (p.type === "path" && p.default !== "" && p.default != null) {
                        const v = String(p.default).trim();
                        if (!/^https?:\/\/.+/i.test(v) && !/^s3:\/\/[^/\s]+(\/.*)?$/i.test(v))
                            throw new Error(`Parameter "${p.name}": the default must be an http(s):// or s3:// path.`);
                    }
                }
                await saveStep({ inputs: s.inputs, parameters: s.parameters, import_step: 3 });
                setStep(3);
            }
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const goBack = () => { setError(""); setStep((s) => Math.max(1, s - 1)); };

    // Final step: save the chosen container image (if any), finalize, attach to the category.
    const finish = async () => {
        setError(""); setBusy(true);
        try {
            if (dockerMode === "image") {
                if (!imageUrl.trim()) throw new Error("Enter a container image URL.");
                const dres = await apiJSON(`${BASE}/${byopId}/dockerfile`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ imageUrl }) });
                if (dres.warnings && dres.warnings.length) setDockerWarnings(dres.warnings);
            }
            const fin = await apiJSON(`${BASE}/${byopId}/finalize`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ category_id: categoryId }) });
            onDone && onDone(fin);
        } catch (e) { setError(e.message); setBusy(false); }
    };

    const detectedNames = [
        ...(((report && report.inputs) || []).map((i) => i.name)),
        ...(((report && report.parameters) || []).map((p) => p.name)),
    ];
    const curNames = values.map((v) => v.name);
    const missingDetected = detectedNames.filter((n) => n && !curNames.includes(n));

    return (
        <div className="iw-wrap">
            <div className="iw-title">Import a pipeline{categoryName ? ` into “${categoryName}”` : ""}</div>
            <div className="iw-sub">Bring a Nextflow (DSL2) pipeline as a .zip or a GitHub repo. It is validated and gets the platform config; once validated you can release it to all users.</div>

            <div className="iw-steps">
                {STEPS.map((label, i) => {
                    const n = i + 1;
                    const cls = n === step ? "active" : n < step ? "done" : "";
                    return <div key={label} className={`iw-step ${cls}`}><span className="num">{n < step ? "✓" : n}</span>{label}</div>;
                })}
            </div>

            {error && <div className="iw-note err">{error}</div>}
            {dockerWarnings.length > 0 && (
                <div className="iw-note warn"><b>Dockerfile advisories:</b><ul style={{ margin: "6px 0 0 18px" }}>{dockerWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul></div>
            )}

            <div className="iw-panel">
                {step === 1 && (
                    <>
                        <div className="iw-h">1 · Source &amp; parse</div>
                        <div className="iw-field">
                            <label>Pipeline name</label>
                            <input className="iw-input" value={pipelineName} onChange={(e) => setPipelineName(e.target.value)} placeholder="Targeted Editing Analysis" />
                        </div>
                        <div className="iw-tabs">
                            <div className={`iw-tab ${sourceTab === "github" ? "active" : ""}`} onClick={() => setSourceTab("github")}>From GitHub</div>
                            <div className={`iw-tab ${sourceTab === "zip" ? "active" : ""}`} onClick={() => setSourceTab("zip")}>Upload .zip</div>
                        </div>
                        {sourceTab === "zip" ? (
                            <div className="iw-field"><label>Pipeline .zip (entry .nf + modules)</label><input type="file" accept=".zip" onChange={(e) => setZipFile(e.target.files[0] || null)} /></div>
                        ) : (
                            <>
                                <div className="iw-field"><label>GitHub repository URL</label><input className="iw-input" value={githubUrl} onChange={(e) => setGithubUrl(e.target.value)} placeholder="https://github.com/owner/repo  (or …/tree/<branch-or-tag>)" /></div>
                                <div className="iw-field"><label>Branch or tag (optional)</label><input className="iw-input" value={githubRef} onChange={(e) => setGithubRef(e.target.value)} placeholder="e.g. main, dev, 3.10.0 — overrides the URL's /tree" /></div>
                                <div className="iw-field"><label>Personal access token (optional — for private repos)</label><input className="iw-input" type="password" autoComplete="off" value={githubToken} onChange={(e) => setGithubToken(e.target.value)} placeholder="ghp_… / github_pat_…" /></div>
                                <div className="iw-note info">A token is only needed for <b>private</b> repositories. It is used once to download the code and is <b>never stored</b>. Use a classic PAT with the <code>repo</code> scope, or a fine-grained token with <b>Contents: read</b> for the repo.</div>
                            </>
                        )}
                        <div className="iw-note info"><b>Note:</b> The pipeline's <code>nextflow.config</code> is <b>kept</b> and security-scanned — CloudGenomics layers its own execution settings (AWS Batch executor, queues, resource caps) on top at launch. You'll set the container and parameters in later steps.</div>

                        {report && <ParseReport report={report} />}
                        {report && mainScriptCandidates.length > 1 && (
                            <div className="iw-field" style={{ marginTop: 14 }}>
                                <label>Main Nextflow script — multiple .nf files were found; choose the entry script to run</label>
                                <select className="iw-select" value={mainScript} onChange={(e) => chooseMain(e.target.value)}>
                                    <option value="">-- Select the entry script --</option>
                                    {mainScriptCandidates.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}
                        {report && mainScriptCandidates.length === 1 && <div className="iw-note info" style={{ marginTop: 14 }}>Entry script detected: <b>{mainScript}</b></div>}
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className="iw-h">2 · Inputs &amp; parameters</div>
                        <div className="iw-sub">For each value the pipeline needs, choose whether it's an <b>Input</b> (a file the user uploads at launch) or a <b>Parameter</b> (a value set at launch). For parameters you can enter a <b>value now</b> — it's stored as the default and is shown &amp; editable on the launch page. These were auto-detected — adjust the kind, add, or remove as needed.</div>
                        <InputsParamsTable values={values} setValues={setValues} />
                        {missingDetected.length > 0 && <div className="iw-note warn" style={{ marginTop: 12 }}>The pipeline references these values that aren't in the list: <b>{missingDetected.join(", ")}</b>. Add them as a Parameter or Input.</div>}
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className="iw-h">3 · Container image</div>
                        {report && report.containersComplete ? (
                            <div className="iw-sub">Every step in this pipeline already declares its own container, so <b>no image is needed</b> — CloudGenomics runs each step in its own container. You may still point to an existing image to override that.</div>
                        ) : (
                            <div className="iw-sub">A container image is <b>optional</b> for off-the-shelf pipelines. If every step declares its own container (e.g. in <code>conf/*.config</code>), choose <b>No image needed</b>. Otherwise point to an existing <b>container image</b> (Docker Hub, Quay.io, GHCR, or ECR) to use as the default step container. Images are used as-is — CloudGenomics does not build images from Dockerfiles.</div>
                        )}
                        <div className="iw-tabs">
                            <div className={`iw-tab ${dockerMode === "none" ? "active" : ""}`} onClick={() => setDockerMode("none")}>No image needed{report && report.containersComplete ? " (recommended)" : ""}</div>
                            <div className={`iw-tab ${dockerMode === "image" ? "active" : ""}`} onClick={() => setDockerMode("image")}>Use an existing image</div>
                        </div>
                        {dockerMode === "none" ? (
                            <div className="iw-note ok">No default image will be set. The head job runs on the CloudGenomics Nextflow launcher and every step pulls the container it declares (in the pipeline or its config). Make sure each step has a container, then click <b>Finish import</b>.</div>
                        ) : (
                            <>
                                <div className="iw-field">
                                    <label>Container image URL</label>
                                    <input className="iw-input" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="quay.io/biocontainers/samtools:1.19--h50ea8bc_0" />
                                </div>
                                <div className="iw-note warn">The image must be <b>publicly pullable</b> (or, for ECR, accessible to the CloudGenomics compute role). It is used as-is — not built or scanned by CloudGenomics.</div>
                            </>
                        )}
                    </>
                )}

                <div className="iw-actions">
                    <button className="iw-btn ghost" onClick={step === 1 ? (onCancel || goBack) : goBack} disabled={busy}>{step === 1 ? "Cancel" : "Back"}</button>
                    {step === 1 ? (
                        !report
                            ? <button className="iw-btn primary" onClick={startImport} disabled={busy}>{busy ? <span className="iw-spin" /> : "Parse pipeline"}</button>
                            : <button className="iw-btn primary" onClick={proceedFromStep1} disabled={busy || (mainScriptCandidates.length > 1 && !mainScript)}>{busy ? <span className="iw-spin" /> : "Continue"}</button>
                    ) : step < 3 ? (
                        <button className="iw-btn primary" onClick={goNext} disabled={busy}>{busy ? <span className="iw-spin" /> : "Save & continue"}</button>
                    ) : (
                        <button className="iw-btn primary" onClick={finish} disabled={busy}>{busy ? <span className="iw-spin" /> : "Finish import"}</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ParseReport({ report }) {
    const flags = report.resourceFlags || [];
    const over = flags.filter((f) => f.over);
    const dyn = flags.filter((f) => f.dynamic);
    const queues = report.queueFlags || [];
    const configScan = report.configScan || null;
    return (
        <div style={{ marginTop: 18 }}>
            <div className="iw-note ok">Parsed the pipeline. Detected <b>{(report.inputs || []).length}</b> input file(s) and <b>{(report.parameters || []).length}</b> parameter(s) across <b>{(report.processes || []).length}</b> process(es) — review them on the next step.</div>
            {(report.warnings || []).map((w, i) => <div key={i} className="iw-note warn">{w}</div>)}
            {over.length > 0 && (
                <div className="iw-note err"><b>Resource limits exceeded</b> — CloudGenomics caps each process at 32 vCPU / 96 GB; these requests will be clamped:
                    <ul style={{ margin: "6px 0 0 18px" }}>{over.map((f, i) => <li key={i}><code>{f.process}</code>: {f.reason}</li>)}</ul>
                </div>
            )}
            {dyn.length > 0 && <div className="iw-note warn">Dynamic resource directives in {dyn.map((f) => f.process).join(", ")} — capped to 32 vCPU / 96 GB at runtime.</div>}
            {queues.length > 0 && <div className="iw-note warn"><b>Queue directives found</b> in {queues.map((q) => q.process).join(", ")} — ignored; the platform assigns queues automatically.</div>}
            {configScan && <div className="iw-note info"><b>Config files kept &amp; security-scanned</b> ({configScan.scanned} checked). The pipeline's <code>nextflow.config</code> is preserved; CloudGenomics layers its AWS Batch execution settings on top at launch.</div>}
        </div>
    );
}
